// Chemin: C:\gestion-copro\src\pages\vigie\FAQVigiePage.tsx
// Session 149 : Page FAQ pour les utilisateurs VIGIE
// Session 151 : Refonte contenu — SCUD universel (tout interlocuteur), scénarios élargis,
//               références "Nous contacter" (remplace BoutonAideFlottant supprimé)
// Session 154 : Enrichissement majeur — 34 questions couvrant toutes les fonctionnalités
// Session 182 : Ajout 11 questions (charges bailleur, etc.) — Total 45 questions
// Session 186 : Ajout 7 questions (RGPD export, résiliation, rétractation, recharge, suppression compte)
//               "Nous contacter" → "Avis & idées" partout — Total 52 questions
// Session 236 : Ajout 5 questions (dossiers objet central, PDF > 5 Mo, suivi bailleur, vérification comptable module,
//               charges récupérables bailleur, aide contextuelle modules) — Total 57 questions
// Session 245 : Ajout catégorie "Ma copropriété" — 4 questions (profil, tantièmes, syndic, correction données) — Total 61 questions

import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  HelpCircle,
  ChevronDown,
  Shield,
  MessageSquare,
  FileText,
  CreditCard,
  AlertTriangle,
  Search,
  BookOpen,
  Brain,
  Home,
  Lock,
  Smartphone,
  Download,
  Building2,
} from 'lucide-react';

// ============================================
// TYPES
// ============================================

interface FAQItem {
  question: string;
  reponse: React.ReactNode;
}

interface FAQCategorie {
  titre: string;
  icone: React.ReactNode;
  couleur: string;
  items: FAQItem[];
}

// ============================================
// DONNÉES FAQ
// ============================================

const faqCategories: FAQCategorie[] = [
  {
    titre: 'Premiers pas',
    icone: <BookOpen className="w-5 h-5" />,
    couleur: 'blue',
    items: [
      {
        question: 'Comment commencer à utiliser Copro Protect ?',
        reponse: (
          <div className="space-y-2">
            <p>
              Après votre inscription, SENTINELLE vous accueille avec un onboarding conversationnel.
              Répondez à ses questions pour configurer votre espace : nom de votre copropriété, syndic, 
              situation actuelle, lots et tantièmes, date de livraison si votre résidence est neuve.
            </p>
            <p>
              À partir de ces informations, SENTINELLE calcule automatiquement vos garanties constructeur,
              évalue votre situation et vous propose un plan d'action personnalisé. Ensuite, vous pouvez 
              directement poser vos questions à SENTINELLE ou explorer les modules dans le menu de gauche.
            </p>
          </div>
        ),
      },
      {
        question: "C'est quoi SENTINELLE ?",
        reponse: (
          <div className="space-y-2">
            <p>
              SENTINELLE est votre copilote intelligent, spécialisé en droit de la copropriété.
              Ce n'est pas un simple chatbot : c'est un assistant qui connaît votre situation personnelle
              (vos lots, vos tantièmes, votre syndic, vos garanties, vos documents archivés) et qui 
              adapte chaque réponse à votre contexte réel.
            </p>
            <p>
              Concrètement, SENTINELLE peut :
            </p>
            <ul className="list-disc list-inside space-y-1 text-gray-600">
              <li>Analyser n'importe quel document de copropriété (courrier, appel de fonds, PV d'AG, contrat, devis, DPE...)</li>
              <li>Rédiger des courriers diplomatiques et juridiquement sûrs à tout interlocuteur</li>
              <li>Détecter les anomalies dans vos documents (montants incohérents, clauses manquantes...)</li>
              <li>Vous alerter quand une échéance approche (garantie, relance, AG...)</li>
              <li>Vérifier vos appels de charges par rapport à vos tantièmes</li>
              <li>Identifier les membres de votre conseil syndical depuis un PV d'AG</li>
            </ul>
          </div>
        ),
      },
      {
        question: "Qu'est-ce que la méthode SCUD ?",
        reponse: (
          <div className="space-y-2">
            <p>
              SCUD signifie <strong>Strategic Communication for Diplomatic Use</strong>. C'est la méthode 
              utilisée par SENTINELLE pour rédiger vos courriers. Elle repose sur 5 piliers :
            </p>
            <ul className="list-disc list-inside space-y-1 text-gray-600">
              <li><strong>Posture de questionnement</strong> — on pose des questions, jamais d'accusation</li>
              <li><strong>Reconnaissance du rôle</strong> — on valorise le professionnalisme de l'interlocuteur</li>
              <li><strong>Questions plutôt qu'affirmations</strong> — protection juridique maximale</li>
              <li><strong>Documentation factuelle</strong> — dates, faits, preuves, sans interprétation</li>
              <li><strong>Remerciement préalable</strong> — reconnaître ce qui a déjà été fait</li>
            </ul>
            <p>
              Résultat : des courriers auxquels votre interlocuteur — syndic, conseil syndical, 
              promoteur, prestataire, assurance, collectivité — est moralement obligé de répondre, 
              tout en vous protégeant juridiquement. Vous ne pouvez jamais être accusé de diffamation 
              ou de harcèlement avec un courrier SCUD.
            </p>
          </div>
        ),
      },
      {
        question: 'À qui puis-je écrire avec Copro Protect ?',
        reponse: (
          <div className="space-y-2">
            <p>
              SENTINELLE peut vous aider à rédiger un courrier à <strong>tout interlocuteur</strong> lié 
              à votre copropriété. La méthode SCUD s'adapte automatiquement au type de destinataire :
            </p>
            <ul className="list-disc list-inside space-y-1 text-gray-600">
              <li><strong>Syndic</strong> — gestion courante, comptes, travaux, transparence</li>
              <li><strong>Conseil syndical</strong> — demander un bilan d'activité, interroger sur le suivi des garanties, la préparation de l'AG</li>
              <li><strong>Promoteur / constructeur</strong> — réserves, malfaçons, garanties (GPA, biennale, décennale)</li>
              <li><strong>Prestataires</strong> — qualité de service, respect du contrat, relances</li>
              <li><strong>Assurances</strong> — déclarations de sinistre, suivi de dossier, demande d'attestation</li>
              <li><strong>Notaire / avocat</strong> — demandes de précisions, relances sur un dossier</li>
              <li><strong>Collectivités</strong> — mairie, urbanisme, voirie, réseaux</li>
              <li><strong>Autres copropriétaires</strong> — nuisances, non-respect du règlement de copropriété</li>
            </ul>
            <p>
              Décrivez simplement votre situation à SENTINELLE et elle adaptera le ton, 
              la stratégie et les références juridiques en fonction de l'interlocuteur.
            </p>
          </div>
        ),
      },
      {
        question: 'SENTINELLE connaît-elle ma situation personnelle ?',
        reponse: (
          <div className="space-y-2">
            <p>
              Oui, et c'est ce qui fait toute la différence. Dès l'onboarding, SENTINELLE enregistre 
              votre situation : type de copropriété, syndic, promoteur, lots avec tantièmes, statut 
              occupant ou bailleur, dates de garantie.
            </p>
            <p>
              Ensuite, chaque document que vous archivez enrichit sa connaissance : montants d'appels 
              de fonds, résolutions votées en AG, noms des membres du conseil syndical, contenu de 
              vos échanges avec le syndic. Quand SENTINELLE rédige un courrier, elle cite vos vrais 
              tantièmes, votre vrai numéro de lot, les vrais montants — pas des formules génériques.
            </p>
            <p>
              Plus vous utilisez Copro Protect, plus SENTINELLE devient précise et pertinente.
            </p>
          </div>
        ),
      },
      {
        question: 'Puis-je utiliser Copro Protect sur mobile ou tablette ?',
        reponse: (
          <p>
            Copro Protect est accessible depuis un navigateur web sur ordinateur, tablette et smartphone.
            L'interface s'adapte à la taille de votre écran. Pour le confort d'utilisation, nous 
            recommandons un écran de tablette ou d'ordinateur pour les analyses de documents et la 
            rédaction de courriers. La dictée vocale fonctionne aussi sur tablette et mobile.
          </p>
        ),
      },
      {
        question: 'Puis-je partager mon compte avec un autre copropriétaire ?',
        reponse: (
          <p>
            Non, chaque compte est strictement personnel. Un compte = un copropriétaire = une situation 
            unique (lots, tantièmes, documents, correspondances). Partager un compte mélangerait vos 
            données et compromettrait la pertinence des analyses de SENTINELLE. Si un autre copropriétaire 
            souhaite utiliser Copro Protect, il peut créer son propre compte VIGIE.
          </p>
        ),
      },
    ],
  },
  // Session 245 — Catégorie "Ma copropriété" (profil, tantièmes, syndic, correction données onboarding)
  {
    titre: 'Ma copropriété',
    icone: <Building2 className="w-5 h-5" />,
    couleur: 'teal',
    items: [
      {
        question: 'Où puis-je voir et modifier les informations de ma copropriété ?',
        reponse: (
          <div className="space-y-2">
            <p>
              Toutes les informations renseignées lors de votre onboarding sont accessibles et 
              modifiables depuis la page <strong>Ma copropriété</strong>, dans le menu de gauche 
              (icône bâtiment). Vous y retrouvez :
            </p>
            <ul className="list-disc list-inside space-y-1 text-gray-600">
              <li>Le nom et l'adresse de votre résidence</li>
              <li>Le type de copropriété (neuve ou ancienne) et la date de livraison</li>
              <li>Le nom de votre syndic, son email, son téléphone et votre relation avec lui</li>
              <li>Le statut de votre conseil syndical</li>
              <li>Le nom du promoteur (si résidence neuve)</li>
              <li>Vos lots avec leur nature, leur numéro et leurs tantièmes</li>
              <li>Le dénominateur total des tantièmes de la copropriété</li>
            </ul>
            <p>
              Passez votre souris sur n'importe quel champ — un crayon apparaît pour le modifier.
              La sauvegarde est immédiate, champ par champ.
            </p>
          </div>
        ),
      },
      {
        question: 'Pourquoi le total des tantièmes est-il si important ?',
        reponse: (
          <div className="space-y-2">
            <p>
              Le <strong>total des tantièmes de la copropriété</strong> (aussi appelé dénominateur) 
              est la base de tous les calculs de charges. Il figure dans votre règlement de 
              copropriété — souvent 1 000 ou 10 000.
            </p>
            <p>
              SENTINELLE l'utilise pour calculer votre quote-part : si vous possédez 120 tantièmes 
              sur un total de 1 000, votre quote-part est de 12 %. Toute vérification d'appel de 
              fonds, de répartition de charges ou de calcul de charges récupérables repose sur 
              ce chiffre. S'il est faux, tous les calculs sont silencieusement faux.
            </p>
            <p>
              Si vous avez saisi une valeur incorrecte lors de l'onboarding, corrigez-la immédiatement 
              dans <strong>Ma copropriété</strong> — c'est le champ "Total tantièmes copropriété" 
              dans la section "Mes lots & tantièmes".
            </p>
          </div>
        ),
      },
      {
        question: "J'ai fait une erreur lors de l'onboarding. Comment corriger ?",
        reponse: (
          <div className="space-y-2">
            <p>
              Rendez-vous dans <strong>Ma copropriété</strong> (menu de gauche). Vous pouvez 
              corriger n'importe quelle information sans contrainte et sans perdre vos données 
              existantes — documents, courriers et dossiers ne sont pas affectés.
            </p>
            <p>
              Cas courants à corriger :
            </p>
            <ul className="list-disc list-inside space-y-1 text-gray-600">
              <li><strong>Dénominateur erroné</strong> (ex : 1002 au lieu de 1000) — section "Mes lots & tantièmes"</li>
              <li><strong>Tantièmes d'un lot manquants ou faux</strong> — cliquez "Modifier mes lots"</li>
              <li><strong>Nom ou email du syndic à mettre à jour</strong> — section "Mon syndic"</li>
              <li><strong>Date de livraison incorrecte</strong> — section "Ma copropriété" (recalcule automatiquement vos garanties)</li>
            </ul>
            <p>
              Après chaque correction, SENTINELLE utilisera les nouvelles valeurs pour toutes 
              ses prochaines analyses et courriers.
            </p>
          </div>
        ),
      },
      {
        question: 'Comment ajouter ou supprimer un lot ?',
        reponse: (
          <div className="space-y-2">
            <p>
              Dans la page <strong>Ma copropriété</strong>, section "Mes lots & tantièmes", 
              cliquez sur <strong>"Modifier mes lots"</strong>. Vous pouvez alors :
            </p>
            <ul className="list-disc list-inside space-y-1 text-gray-600">
              <li>Ajouter un nouveau lot (type, numéro optionnel, tantièmes)</li>
              <li>Modifier la nature, le numéro ou les tantièmes d'un lot existant</li>
              <li>Supprimer un lot avec la corbeille en bout de ligne</li>
            </ul>
            <p>
              La quote-part affichée en haut de la section se met à jour automatiquement 
              dès que vous enregistrez. Si vous possédez plusieurs lots (par exemple un 
              appartement et un parking), listez-les séparément — SENTINELLE les prend 
              tous en compte dans ses calculs de charges.
            </p>
          </div>
        ),
      },
    ],
  },
  {
    titre: 'Intelligence SENTINELLE',
    icone: <Brain className="w-5 h-5" />,
    couleur: 'indigo',
    items: [
      {
        question: 'Comment faire analyser un document par SENTINELLE ?',
        reponse: (
          <div className="space-y-2">
            <p>
              Dans le chat SENTINELLE, glissez-déposez votre document (PDF, image, Word) dans la zone prévue, 
              ou cliquez sur le bouton trombone. SENTINELLE analyse automatiquement le contenu et vous donne :
            </p>
            <ul className="list-disc list-inside space-y-1 text-gray-600">
              <li>Un résumé clair du document</li>
              <li>Les données clés extraites (montants, dates, noms, références)</li>
              <li>Les anomalies ou points d'attention détectés</li>
              <li>Des propositions d'actions concrètes (archiver, rédiger un courrier, créer un dossier...)</li>
            </ul>
            <p>
              Le document est ensuite proposé à l'archivage dans votre GED, automatiquement catégorisé 
              et classé. Vous gardez toujours le contrôle : rien n'est archivé sans votre validation.
            </p>
          </div>
        ),
      },
      {
        question: 'SENTINELLE peut-elle rédiger un courrier à ma place ?',
        reponse: (
          <div className="space-y-2">
            <p>
              Oui. Décrivez votre situation à SENTINELLE en précisant votre interlocuteur (syndic, 
              conseil syndical, promoteur, prestataire...) et elle vous proposera un courrier complet 
              rédigé selon la méthode SCUD.
            </p>
            <p>
              Le courrier est pré-rempli avec vos données réelles : votre nom, votre adresse, 
              le numéro de lot, les références des documents concernés. Vous pouvez le modifier, 
              puis l'enregistrer dans vos correspondances.
            </p>
            <p>
              <strong>Important :</strong> le courrier n'est jamais envoyé automatiquement — c'est vous 
              qui le copiez-collez dans votre messagerie ou qui l'imprimez. Vous gardez le contrôle total.
            </p>
          </div>
        ),
      },
      {
        question: "Combien d'analyses ai-je droit ?",
        reponse: (
          <div className="space-y-2">
            <p>
              Votre abonnement inclut <strong>100 analyses par an</strong>. Chaque question posée à SENTINELLE 
              et chaque document analysé consomme une analyse. Vous pouvez suivre votre consommation 
              depuis le tableau de bord.
            </p>
            <p>
              Un bandeau d'alerte apparaît quand vous atteignez 80% de votre quota. Si vous atteignez 
              la limite, SENTINELLE vous le signale et vous propose un pack de recharge 
              de 100 analyses supplémentaires à 29 €. Votre quota se renouvelle automatiquement 
              à la date anniversaire de votre abonnement.
            </p>
          </div>
        ),
      },
      {
        question: 'Pourquoi SENTINELLE me propose des suggestions personnalisées à l\'accueil ?',
        reponse: (
          <div className="space-y-2">
            <p>
              À chaque ouverture du chat, SENTINELLE analyse votre situation en temps réel : 
              vos échéances de garantie, vos courriers en attente de réponse, vos dossiers ouverts, 
              les documents manquants dans votre dossier.
            </p>
            <p>
              Les boutons de suggestions que vous voyez à l'accueil ne sont pas des phrases génériques : 
              ils reflètent ce qui est pertinent pour vous à cet instant précis. Par exemple :
            </p>
            <ul className="list-disc list-inside space-y-1 text-gray-600">
              <li>"La GPA expire dans 45 jours" — si votre garantie approche</li>
              <li>"1 courrier en attente de validation" — si vous avez un brouillon non finalisé</li>
              <li>"J'ai trouvé un PV d'AG dans tes documents" — si SENTINELLE détecte un PV exploitable</li>
            </ul>
            <p>
              Il suffit de cliquer sur une suggestion pour lancer la conversation sur ce sujet.
            </p>
          </div>
        ),
      },
      {
        question: 'SENTINELLE peut-elle détecter les membres de mon conseil syndical ?',
        reponse: (
          <div className="space-y-2">
            <p>
              Oui, si vous avez archivé un PV d'assemblée générale dans vos documents. SENTINELLE 
              lit les données clés extraites du PV (président, secrétaire, scrutateurs, membres élus) 
              et vous propose de créer automatiquement une fiche contact pour chaque membre identifié.
            </p>
            <p>
              Cela vous permet ensuite de leur écrire directement via SENTINELLE si nécessaire — 
              par exemple si le conseil syndical est inactif et que vous souhaitez demander 
              un bilan de ses actions.
            </p>
          </div>
        ),
      },
      {
        question: 'SENTINELLE peut-elle comparer un courrier reçu avec ce que j\'avais envoyé ?',
        reponse: (
          <div className="space-y-2">
            <p>
              Oui, c'est l'Intelligence Correspondance. Quand vous archivez un courrier reçu 
              (par exemple une réponse du syndic), SENTINELLE détecte automatiquement s'il s'agit 
              d'une réponse à un courrier que vous aviez envoyé.
            </p>
            <p>
              Elle compare alors les deux documents point par point et vous indique :
            </p>
            <ul className="list-disc list-inside space-y-1 text-gray-600">
              <li>Quels points de votre demande ont été traités</li>
              <li>Quels points ont été partiellement traités</li>
              <li>Quels points ont été complètement ignorés</li>
            </ul>
            <p>
              Si des points restent sans réponse, SENTINELLE peut générer automatiquement 
              une relance ciblée qui ne reprend que les questions restées en suspens.
            </p>
          </div>
        ),
      },
      {
        question: 'Comment fonctionne la vérification comptable ?',
        reponse: (
          <div className="space-y-2">
            <p>
              Déposez votre relevé de charges ou appel de fonds dans le chat SENTINELLE. 
              Elle vérifie automatiquement :
            </p>
            <ul className="list-disc list-inside space-y-1 text-gray-600">
              <li>La cohérence du montant appelé par rapport à vos tantièmes</li>
              <li>Les écarts avec le budget prévisionnel voté en AG</li>
              <li>Les postes de charges inhabituels ou en forte augmentation</li>
              <li>La conformité avec les règles de répartition du règlement de copropriété</li>
            </ul>
            <p>
              SENTINELLE produit un score d'anomalie et un rapport détaillé. Si des incohérences 
              sont détectées, elle vous propose de rédiger un courrier au syndic pour demander 
              des explications — avec les bons chiffres et les bonnes références.
            </p>
          </div>
        ),
      },
      {
        question: 'Puis-je utiliser la dictée vocale ?',
        reponse: (
          <div className="space-y-2">
            <p>
              Oui, un bouton de dictée vocale (micro) est disponible dans le chat SENTINELLE. 
              Cliquez dessus, parlez, et votre message sera transcrit automatiquement.
            </p>
            <p>
              C'est particulièrement pratique pour décrire une situation complexe sans avoir à tout 
              taper, ou sur tablette/mobile où la saisie au clavier est moins confortable. La dictée 
              fonctionne en français et prend en charge les termes courants de la copropriété.
            </p>
          </div>
        ),
      },
      {
        question: 'Je suis propriétaire bailleur. SENTINELLE peut-elle calculer mes charges récupérables ?',
        reponse: (
          <div className="space-y-2">
            <p>
              Oui, c'est l'un des cas d'usage les plus puissants de SENTINELLE pour les bailleurs. 
              Pour un calcul fiable, archivez dans votre GED :
            </p>
            <ul className="list-disc list-inside space-y-1 text-gray-600">
              <li><strong>Votre bail</strong> — SENTINELLE en extrait le loyer, les provisions pour charges et la date de début</li>
              <li><strong>Le règlement de copropriété</strong> — pour les clés de répartition par poste de dépense</li>
              <li><strong>Vos quittances de loyer</strong> — SENTINELLE totalise automatiquement les provisions versées par votre locataire</li>
              <li><strong>Les appels de fonds</strong> — pour connaître les charges réelles de la copropriété</li>
              <li><strong>Votre avis de taxe foncière</strong> — pour récupérer la TEOM (récupérable à 100%)</li>
            </ul>
            <p>
              Ensuite, demandez simplement : "Régularise mes charges" ou "Calcule mes charges récupérables". 
              SENTINELLE croise toutes les données, applique les bonnes clés de répartition, distingue les charges 
              récupérables des non récupérables, et produit un décompte complet.
            </p>
          </div>
        ),
      },
      {
        question: 'SENTINELLE peut-elle générer une régularisation de charges pour mon locataire ?',
        reponse: (
          <div className="space-y-2">
            <p>
              Oui. Après le calcul des charges récupérables, SENTINELLE produit un courrier de 
              régularisation complet qui inclut :
            </p>
            <ul className="list-disc list-inside space-y-1 text-gray-600">
              <li>Le décompte détaillé (provisions versées vs charges réelles)</li>
              <li>Le trop-perçu ou le complément à régler</li>
              <li>L'ajustement des provisions mensuelles pour la période suivante</li>
              <li>Le montant du prochain loyer après régularisation</li>
              <li>Les références légales (article 23 de la loi du 6 juillet 1989)</li>
            </ul>
            <p>
              Le courrier est automatiquement classé comme "Notification" (et non comme une simple demande 
              d'information) car il a une portée juridique. Vous pouvez le modifier avant de l'envoyer.
            </p>
          </div>
        ),
      },
      {
        question: 'Puis-je exporter les résultats de SENTINELLE en PDF ?',
        reponse: (
          <div className="space-y-2">
            <p>
              Oui. Quand SENTINELLE produit un résultat structuré — par exemple un tableau de charges 
              récupérables, une vérification comptable ou une analyse comparative — un bouton 
              "Exporter en PDF" apparaît sous le message.
            </p>
            <p>
              Le PDF généré est un document professionnel avec en-tête Copro Protect, le nom de votre 
              copropriété, la date, et le contenu complet de l'analyse (tableaux, calculs, recommandations). 
              Vous pouvez le conserver comme pièce justificative ou le joindre à un courrier.
            </p>
            <p>
              Ce bouton n'apparaît que pour les résultats qui s'y prêtent — pas pour une simple 
              réponse conversationnelle.
            </p>
          </div>
        ),
      },
      {
        question: 'Je suis en LMNP, SCI ou LMP. SENTINELLE peut-elle m\'aider ?',
        reponse: (
          <div className="space-y-2">
            <p>
              Oui. SENTINELLE adapte automatiquement ses analyses et ses conseils selon votre 
              régime fiscal. Elle connaît les spécificités de chaque statut :
            </p>
            <ul className="list-disc list-inside space-y-1 text-gray-600">
              <li><strong>Nu-propriétaire classique</strong> — revenus fonciers, micro-foncier ou régime réel, déduction des charges de copropriété sur la 2044</li>
              <li><strong>LMNP</strong> — régime BIC, amortissement du bien, charges déductibles spécifiques, CFE, comptabilité d'engagement</li>
              <li><strong>LMP</strong> — seuils de revenus, cotisations sociales, plus-value professionnelle</li>
              <li><strong>SCI à l'IR</strong> — transparence fiscale, quote-part associés, déclaration 2072 puis report 2044</li>
              <li><strong>SCI à l'IS</strong> — comptabilité société, amortissement, IS sur bénéfices</li>
              <li><strong>Nue-propriété / Usufruit</strong> — répartition des charges entre nu-propriétaire et usufruitier</li>
            </ul>
            <p>
              Quand vous demandez un calcul de charges récupérables ou une aide fiscale, 
              SENTINELLE prend en compte votre régime pour vous donner les bons montants 
              déductibles et les bonnes lignes de déclaration. Si votre régime n'est pas 
              encore renseigné, elle vous le demandera.
            </p>
          </div>
        ),
      },
      {
        question: 'SENTINELLE peut-elle m\'aider pour ma déclaration fiscale ?',
        reponse: (
          <div className="space-y-2">
            <p>
              SENTINELLE vous aide à préparer votre déclaration en identifiant précisément 
              ce qui est déductible parmi vos charges de copropriété. Concrètement :
            </p>
            <ul className="list-disc list-inside space-y-1 text-gray-600">
              <li>Elle distingue les charges déductibles des charges non déductibles selon votre régime</li>
              <li>Elle calcule les provisions déduites en année N et la régularisation en année N+1</li>
              <li>Elle vous indique les lignes exactes de votre formulaire (2044, 2042, 2072 selon le cas)</li>
              <li>Elle intègre la TEOM (récupérable sur le locataire ET déductible fiscalement)</li>
            </ul>
            <p>
              <strong>Important :</strong> SENTINELLE n'est pas un expert-comptable et ne remplace pas 
              un conseil fiscal professionnel. Elle vous fournit les données structurées et les 
              montants issus de vos documents réels pour faciliter votre déclaration ou celle 
              de votre comptable. En cas de doute sur un montage fiscal, consultez un professionnel.
            </p>
          </div>
        ),
      },
      {
        question: 'Comment se passe la première utilisation de Copro Protect ?',
        reponse: (
          <div className="space-y-2">
            <p>
              Dès votre première connexion, SENTINELLE vous accueille et vous guide pas à pas 
              dans un <strong>onboarding conversationnel</strong>. Pas de formulaires à remplir, 
              pas de documentation à lire — c'est une conversation naturelle.
            </p>
            <p>
              SENTINELLE vous pose des questions simples : le nom de votre copropriété, sa date 
              de livraison, votre syndic, le nombre de lots que vous possédez, si vous êtes 
              occupant ou bailleur. Chaque réponse configure automatiquement votre espace.
            </p>
            <p>
              Si votre résidence a moins de 10 ans, SENTINELLE calcule immédiatement vos 
              garanties constructeur (GPA, biennale, décennale) et vous alerte si une échéance 
              approche. À la fin de l'onboarding, votre espace est prêt et SENTINELLE connaît 
              votre situation — vous pouvez commencer à travailler tout de suite.
            </p>
          </div>
        ),
      },
      {
        question: 'SENTINELLE peut-elle analyser automatiquement mes documents ?',
        reponse: (
          <div className="space-y-2">
            <p>
              Oui. Quand vous déposez un document dans <strong>Mes documents</strong>, SENTINELLE 
              l'analyse automatiquement et fait le travail de classement à votre place :
            </p>
            <ul className="list-disc list-inside space-y-1 text-gray-600">
              <li><strong>Classification</strong> — elle identifie le type (facture, PV, contrat, appel de fonds…) et la catégorie (comptabilité, travaux, juridique…)</li>
              <li><strong>Extraction</strong> — elle repère les données clés : dates, montants, noms, numéros de lot, tantièmes</li>
              <li><strong>Résumé</strong> — elle produit une synthèse du document en quelques lignes</li>
              <li><strong>Liaison</strong> — elle suggère un rattachement à un dossier existant si le contenu correspond</li>
            </ul>
            <p>
              Vous pouvez aussi déposer un document directement dans le chat SENTINELLE pour 
              obtenir une analyse approfondie avec des recommandations d'action. Par exemple, 
              déposez un appel de fonds et demandez "est-ce que c'est cohérent avec le budget voté".
            </p>
          </div>
        ),
      },
      {
        question: 'Comment fonctionne la vérification comptable ?',
        reponse: (
          <div className="space-y-2">
            <p>
              Le module <strong>Vérification comptable</strong> permet à SENTINELLE d'analyser 
              vos documents financiers de copropriété pour détecter des anomalies que vous 
              n'auriez pas repérées vous-même :
            </p>
            <ul className="list-disc list-inside space-y-1 text-gray-600">
              <li>Écarts entre le budget voté en AG et les appels de fonds réellement émis</li>
              <li>Incohérences dans la répartition des charges entre copropriétaires</li>
              <li>Erreurs de tantièmes (le total doit correspondre au règlement de copropriété)</li>
              <li>Charges inhabituellement élevées par rapport aux exercices précédents</li>
            </ul>
            <p>
              SENTINELLE produit un rapport structuré avec les anomalies détectées, classées 
              par niveau de gravité. Vous pouvez exporter ce rapport en PDF pour le présenter 
              au syndic ou au conseil syndical. C'est un outil particulièrement utile avant 
              l'assemblée générale.
            </p>
          </div>
        ),
      },
      {
        question: "Comment utiliser le module Ma vérification comptable ?",
        reponse: (
          <div className="space-y-2">
            <p>
              Le module <strong>Ma vérification comptable</strong> (accessible depuis le menu de gauche)
              permet d'organiser vos vérifications par exercice annuel. Voici comment cela fonctionne :
            </p>
            <ul className="list-disc list-inside space-y-1 text-gray-600">
              <li><strong>Créez une vérification</strong> pour l'exercice concerné (ex : 2025 ou 2025-2026)</li>
              <li><strong>Ajoutez vos documents</strong> : appels de fonds, budget prévisionnel, annexes comptables</li>
              <li><strong>SENTINELLE analyse</strong> et calcule un score de conformité de 0 à 100</li>
              <li><strong>Consultez le rapport</strong> : anomalies détectées, montants appelés vs réglés, solde</li>
            </ul>
            <p>
              Le score vous indique en un coup d'il l'état de vos finances de copropriété :
              vert (80 et plus) tout va bien, orange (60-79) à surveiller, rouge (moins de 60) anomalie à investiguer.
              Si des incohérences sont détectées, SENTINELLE peut générer un courrier au syndic
              avec les bons chiffres et les bonnes références.
            </p>
          </div>
        ),
      },
      {
        question: 'SENTINELLE peut-elle m\'alerter sans que je lui demande ?',
        reponse: (
          <div className="space-y-2">
            <p>
              Oui. Dès que vous ouvrez le chat, SENTINELLE analyse votre situation en temps 
              réel et affiche des <strong>suggestions proactives</strong> — des alertes et 
              recommandations basées sur vos données réelles :
            </p>
            <ul className="list-disc list-inside space-y-1 text-gray-600">
              <li><strong>Courrier sans réponse</strong> — "Vous avez envoyé un courrier au syndic il y a 22 jours sans réponse. Voulez-vous relancer ?"</li>
              <li><strong>Échéance proche</strong> — "Votre garantie de parfait achèvement expire dans 45 jours. Avez-vous des réserves à signaler ?"</li>
              <li><strong>Dossier sans action</strong> — "Votre dossier infiltration du 10 janvier n'a pas encore de courrier associé."</li>
              <li><strong>Document manquant</strong> — "Vous n'avez pas encore archivé de PV d'AG — ce document est essentiel pour vos démarches."</li>
            </ul>
            <p>
              Ces suggestions apparaissent en haut du chat sous forme de bulles cliquables. 
              Un clic lance directement la conversation sur le sujet concerné. SENTINELLE 
              ne vous alerte que sur ce qui est pertinent — pas de notifications parasites.
            </p>
          </div>
        ),
      },
    ],
  },
  {
    titre: 'Correspondances & contacts',
    icone: <MessageSquare className="w-5 h-5" />,
    couleur: 'emerald',
    items: [
      {
        question: 'Comment fonctionne le module Mes correspondances ?',
        reponse: (
          <div className="space-y-2">
            <p>
              Ce module organise tous vos échanges écrits, quel que soit l'interlocuteur : syndic, 
              conseil syndical, promoteur, prestataires, assurances, collectivités...
            </p>
            <p>
              Deux types de courriers :
            </p>
            <ul className="list-disc list-inside space-y-1 text-gray-600">
              <li><strong>Courrier sortant</strong> — un courrier que vous envoyez (généré par SENTINELLE ou créé manuellement). Il passe par un workflow : brouillon → finalisé → envoyé.</li>
              <li><strong>Courrier entrant</strong> — un courrier que vous avez reçu. Archivez-le pour que SENTINELLE puisse le comparer avec vos envois et détecter les réponses incomplètes.</li>
            </ul>
            <p>
              SENTINELLE analyse chaque courrier et peut détecter les liens entre eux — par exemple, 
              si un courrier reçu est une réponse à un courrier que vous aviez envoyé.
            </p>
          </div>
        ),
      },
      {
        question: "À quoi sert le module Mes contacts ?",
        reponse: (
          <div className="space-y-2">
            <p>
              C'est votre carnet d'adresses dédié à la copropriété. Enregistrez tous vos interlocuteurs 
              avec leurs coordonnées. Quand vous créez un courrier ou un dossier, vous pouvez 
              les sélectionner directement.
            </p>
            <p>Exemples de contacts à enregistrer :</p>
            <ul className="list-disc list-inside space-y-1 text-gray-600">
              <li>Syndic (cabinet, gestionnaire référent)</li>
              <li>Membres du conseil syndical (président, secrétaire, membres)</li>
              <li>Promoteur / constructeur</li>
              <li>Prestataires (entretien, ascenseur, espaces verts, chauffage...)</li>
              <li>Assurances (copropriété, dommages-ouvrage, votre MRH)</li>
              <li>Notaire, avocat, expert</li>
              <li>Collectivités (mairie, urbanisme)</li>
            </ul>
            <p>
              SENTINELLE peut aussi créer des contacts automatiquement quand elle identifie des personnes 
              dans vos documents — par exemple les membres du CS depuis un PV d'AG.
            </p>
          </div>
        ),
      },
    ],
  },
  {
    titre: 'Documents & dossiers',
    icone: <FileText className="w-5 h-5" />,
    couleur: 'amber',
    items: [
      {
        question: 'Comment organiser mes documents ?',
        reponse: (
          <div className="space-y-2">
            <p>
              Le module <strong>Mes documents</strong> est votre coffre-fort numérique. Chaque document 
              est automatiquement catégorisé par SENTINELLE lors de l'archivage : juridique, comptable, 
              technique, syndic, promoteur, assurance...
            </p>
            <p>
              Vous pouvez ajouter des documents de deux façons :
            </p>
            <ul className="list-disc list-inside space-y-1 text-gray-600">
              <li>Depuis le chat SENTINELLE — déposez un fichier, SENTINELLE l'analyse et propose l'archivage</li>
              <li>Directement depuis le module Mes documents — upload classique</li>
            </ul>
            <p>
              SENTINELLE détecte automatiquement les doublons grâce à une empreinte numérique unique 
              de chaque fichier. Impossible d'archiver deux fois le même document par erreur.
            </p>
          </div>
        ),
      },
      {
        question: "À quoi sert le module Mes dossiers ?",
        reponse: (
          <div className="space-y-2">
            <p>
              Le <strong>dossier</strong> est l'objet central de votre suivi en copropriété. 
              Il regroupe tout ce qui est lié à une même affaire : courriers échangés, documents 
              archivés, chronologie des événements. Par exemple, un dossier "Infiltration terrasse" 
              contiendra les courriers au syndic et au prestataire, les photos, le rapport d'expert 
              et le suivi de la résolution.
            </p>
            <p>
              Quand SENTINELLE génère un courrier lié à un dossier, elle le rattache automatiquement 
              à ce dossier. La chronologie se construit sans effort de votre côté.
            </p>
            <p>
              Chaque dossier a une timeline complète depuis l'ouverture jusqu'à la résolution — 
              c'est votre preuve documentée en cas de litige ou de passage devant une juridiction.
            </p>
          </div>
        ),
      },
      {
        question: "SENTINELLE peut-elle analyser des documents volumineux ?",
        reponse: (
          <div className="space-y-2">
            <p>
              Oui, jusqu'à <strong>25 Mo</strong> pour les PDF. Pour les fichiers entre 5 et 25 Mo 
              (PPPT, DPE, diagnostics techniques...), SENTINELLE bascule automatiquement en 
              <strong>mode texte</strong> : elle extrait le contenu du document et l'analyse 
              sans le voir visuellement. L'analyse reste pertinente — les montants, les 
              scénarios de travaux, les dates sont dans le texte.
            </p>
            <p>
              Pour les fichiers sous 5 Mo, SENTINELLE voit le document visuellement — meilleure 
              qualité d'analyse pour les documents avec tableaux ou mises en page complexes. 
              Si votre fichier est trop volumineux pour être analysé, SENTINELLE vous l'indique 
              avec un message clair et vous suggère de le compresser.
            </p>
            <p>
              <strong>Formats acceptés :</strong> PDF, JPEG, PNG, WebP, GIF, Word, Excel.
            </p>
          </div>
        ),
      },
      {
        question: "À quoi sert le module Mes échéances ?",
        reponse: (
          <div className="space-y-2">
            <p>
              Le module <strong>Mes échéances</strong> centralise toutes vos échéances :
            </p>
            <ul className="list-disc list-inside space-y-1 text-gray-600">
              <li>Dates de garantie constructeur (GPA, biennale, décennale) — calculées automatiquement</li>
              <li>Relances à envoyer — calculées selon le mode d'envoi (email 7 jours, postal 15 jours, LRAR 21 jours)</li>
              <li>Échéances de vos dossiers en cours</li>
              <li>Rappels manuels que vous créez</li>
            </ul>
            <p>
              SENTINELLE croise ces échéances pour ses suggestions proactives. Si un courrier envoyé 
              au syndic n'a pas reçu de réponse après le délai légal, elle vous le signalera 
              automatiquement à l'ouverture du chat.
            </p>
          </div>
        ),
      },
      {
        question: "Comment relancer un interlocuteur qui ne répond pas ?",
        reponse: (
          <div className="space-y-2">
            <p>
              Depuis le module <strong>Mes échéances</strong>, les courriers envoyés sans réponse 
              apparaissent automatiquement avec leur délai restant. Quand le délai est dépassé, 
              un bouton "Relancer" vous permet de lancer directement une relance via SENTINELLE.
            </p>
            <p>
              SENTINELLE analyse alors l'historique complet de vos échanges avec cet interlocuteur 
              et génère une relance graduée selon la méthode SCUD : si c'est la première relance, 
              le ton reste courtois. Si c'est la troisième, SENTINELLE suggère une escalade 
              (passage du email au courrier recommandé, par exemple).
            </p>
            <p>
              Vous pouvez aussi demander directement dans le chat : "Relance le syndic sur mon courrier 
              du 15 janvier" — SENTINELLE retrouvera le courrier concerné et proposera une relance adaptée.
            </p>
          </div>
        ),
      },
      {
        question: "Comment fonctionne la résolution des dossiers et courriers ?",
        reponse: (
          <div className="space-y-2">
            <p>
              Quand un sujet est traité (le syndic a répondu, les travaux sont faits, le litige est clos), 
              vous pouvez le marquer comme <strong>résolu</strong> d'un clic. Cela concerne les courriers, 
              les dossiers et les échéances.
            </p>
            <p>
              Les éléments résolus disparaissent de vos listes courantes — vous travaillez sur le vivant, 
              sans être encombré par l'historique. Mais rien n'est perdu : un onglet "Résolus" vous 
              permet de retrouver tout ce qui a été traité. SENTINELLE aussi ne tient plus compte des 
              éléments résolus dans ses analyses et suggestions, pour rester pertinente.
            </p>
          </div>
        ),
      },
    ],
  },
  {
    titre: 'Garanties & VEFA',
    icone: <Home className="w-5 h-5" />,
    couleur: 'teal',
    items: [
      {
        question: 'Comment SENTINELLE gère-t-elle mes garanties constructeur ?',
        reponse: (
          <div className="space-y-2">
            <p>
              Si votre résidence est neuve, SENTINELLE calcule automatiquement les dates d'expiration 
              de vos trois garanties à partir de la date de livraison renseignée lors de l'onboarding :
            </p>
            <ul className="list-disc list-inside space-y-1 text-gray-600">
              <li><strong>GPA (Garantie de Parfait Achèvement)</strong> — 1 an après la livraison. Couvre tous les défauts signalés à la réception ou apparus dans l'année.</li>
              <li><strong>Biennale (Garantie de bon fonctionnement)</strong> — 2 ans. Couvre les équipements dissociables (volets, robinetterie, chauffage...).</li>
              <li><strong>Décennale</strong> — 10 ans. Couvre les dommages affectant la solidité de l'ouvrage ou le rendant impropre à sa destination.</li>
            </ul>
            <p>
              SENTINELLE vous alerte automatiquement quand une garantie approche de son expiration. 
              Elle adapte ses suggestions : si la GPA expire bientôt, elle vous demandera quels 
              désordres restent à signaler avant qu'il ne soit trop tard.
            </p>
          </div>
        ),
      },
      {
        question: 'Ma copropriété est neuve (VEFA). Quels documents dois-je surveiller ?',
        reponse: (
          <div className="space-y-2">
            <p>
              En construction neuve, plusieurs documents sont critiques et ont des délais :
            </p>
            <ul className="list-disc list-inside space-y-1 text-gray-600">
              <li><strong>PV de réception des parties communes</strong> — liste les réserves à la livraison. C'est le point de départ de vos garanties collectives.</li>
              <li><strong>Attestation d'assurance dommages-ouvrage (DO)</strong> — obligatoire, souscrite par le promoteur. Sans elle, vous êtes vulnérable.</li>
              <li><strong>PV de livraison de votre lot</strong> — avec vos réserves personnelles. Prouve ce que vous avez signalé à la réception.</li>
              <li><strong>Notice descriptive VEFA</strong> — ce qui a été promis contractuellement. Permet de vérifier la conformité.</li>
              <li><strong>Carnet d'entretien de l'immeuble</strong> — obligatoire, fourni par le promoteur au syndic.</li>
            </ul>
            <p>
              Déposez ces documents dans votre GED et SENTINELLE les analysera, détectera les anomalies 
              et surveillera les échéances de garantie associées. Si un document manque, elle vous 
              guidera pour le réclamer auprès du bon interlocuteur (syndic ou promoteur).
            </p>
          </div>
        ),
      },
      {
        question: "J'ai des malfaçons dans mon logement. Comment les signaler efficacement ?",
        reponse: (
          <div className="space-y-2">
            <p>
              SENTINELLE vous guide étape par étape :
            </p>
            <ul className="list-disc list-inside space-y-1 text-gray-600">
              <li><strong>1. Créez un dossier</strong> avec photos et description précise du désordre</li>
              <li><strong>2. Demandez à SENTINELLE</strong> de rédiger un courrier au promoteur (si GPA) ou au syndic (si parties communes)</li>
              <li><strong>3. Archivez les échanges</strong> pour constituer votre dossier de preuves</li>
            </ul>
            <p>
              SENTINELLE adapte automatiquement le courrier selon la garantie applicable : GPA pour 
              les défauts de conformité, biennale pour les équipements, décennale pour les problèmes 
              structurels. Elle vous rappellera les délais si la garantie approche de son terme.
            </p>
          </div>
        ),
      },
    ],
  },
  {
    titre: 'Situations concrètes',
    icone: <Shield className="w-5 h-5" />,
    couleur: 'cyan',
    items: [
      {
        question: 'Mon conseil syndical ne fait rien depuis son élection. Que faire ?',
        reponse: (
          <div className="space-y-2">
            <p>
              C'est une situation fréquente. En tant que copropriétaire, vous avez le droit de demander 
              des comptes au conseil syndical sur son activité. SENTINELLE peut vous aider à rédiger 
              un courrier diplomatique pour :
            </p>
            <ul className="list-disc list-inside space-y-1 text-gray-600">
              <li>Demander un bilan des actions entreprises depuis l'élection</li>
              <li>Interroger sur le suivi des garanties (GPA, décennale) et des réserves</li>
              <li>Vérifier que l'AG a été préparée (ordre du jour, contrôle des comptes)</li>
              <li>Demander si les contrats de prestataires ont été examinés</li>
            </ul>
            <p>
              La méthode SCUD est idéale ici : on pose des questions factuelles sans accuser, 
              tout en documentant l'inaction pour une éventuelle mise en cause ultérieure.
              Si le CS reste muet, SENTINELLE peut vous orienter vers une action directe 
              auprès du syndic.
            </p>
          </div>
        ),
      },
      {
        question: 'Mon syndic ne répond pas à mes courriers. Comment réagir ?',
        reponse: (
          <div className="space-y-2">
            <p>
              Demandez à SENTINELLE de générer une relance. Elle analysera vos échanges précédents 
              et rédigera un courrier de relance SCUD qui rappelle factuellement les demandes restées 
              sans réponse, avec les dates. 
            </p>
            <p>
              Si plusieurs relances restent sans effet, SENTINELLE peut vous orienter vers les recours 
              possibles (lettre recommandée, saisine du conseil syndical, signalement en AG).
              L'important est que chaque étape soit documentée dans votre dossier — c'est 
              votre protection en cas de litige.
            </p>
          </div>
        ),
      },
      {
        question: 'Comment préparer mon AG avec SENTINELLE ?',
        reponse: (
          <div className="space-y-2">
            <p>
              SENTINELLE peut vous accompagner sur toute la préparation :
            </p>
            <ul className="list-disc list-inside space-y-1 text-gray-600">
              <li><strong>Analyser la convocation et l'ordre du jour</strong> — déposez-les dans le chat, SENTINELLE identifie les résolutions importantes et les points de vigilance</li>
              <li><strong>Vérifier les comptes avant le vote</strong> — utilisez la vérification comptable pour détecter les anomalies</li>
              <li><strong>Préparer vos questions</strong> — décrivez vos préoccupations, SENTINELLE les formule de manière claire et factuelle</li>
              <li><strong>Rédiger des résolutions à proposer</strong> — si vous souhaitez inscrire un point à l'ordre du jour</li>
            </ul>
            <p>
              Après l'AG, archivez le PV : SENTINELLE extraira les résolutions votées et pourra 
              suivre leur exécution dans le temps.
            </p>
          </div>
        ),
      },
      {
        question: 'Un prestataire ne respecte pas son contrat. Que faire ?',
        reponse: (
          <p>
            Enregistrez le prestataire dans vos contacts, créez un dossier décrivant le problème, 
            puis demandez à SENTINELLE de rédiger un courrier SCUD. Elle posera les bonnes questions 
            (rappel des engagements contractuels, demande d'explication, délai de régularisation) 
            sans agressivité, ce qui est plus efficace pour obtenir une réponse et constituer 
            un dossier solide si la situation dégénère.
          </p>
        ),
      },
      {
        question: "Je dois déclarer un sinistre à l'assurance de la copropriété",
        reponse: (
          <p>
            Documentez le sinistre dans un dossier (photos, description, date). 
            Puis demandez à SENTINELLE de vous aider à rédiger la déclaration. Elle s'assurera 
            que tous les éléments requis sont présents (nature du sinistre, date de constatation, 
            localisation, dommages constatés) et que le ton reste factuel. Conservez le tout 
            dans un dossier dédié pour suivre l'évolution.
          </p>
        ),
      },
    ],
  },
  {
    titre: 'Propriétaire bailleur',
    icone: <Home className="w-5 h-5" />,
    couleur: 'amber',
    items: [
      {
        question: "À quoi sert le module Suivi bailleur ?",
        reponse: (
          <div className="space-y-2">
            <p>
              Le module <strong>Suivi bailleur</strong> est accessible si vous avez indiqué être 
              propriétaire bailleur lors de l'onboarding. Il vous permet de centraliser le suivi 
              de votre ou vos locations :
            </p>
            <ul className="list-disc list-inside space-y-1 text-gray-600">
              <li>Fiche locataire avec loyer mensuel, provision charges et date d'entrée</li>
              <li>Enregistrement des paiements mois par mois (loyer, charges, régularisation)</li>
              <li>Solde en temps réel : à jour, en retard, impayé</li>
              <li>Historique complet des paiements avec statuts</li>
            </ul>
            <p>
              Si vous êtes bailleur mais que ce module n'apparaît pas dans votre menu, 
              indiquez-le à SENTINELLE dans le chat — elle mettra à jour votre profil.
            </p>
          </div>
        ),
      },
      {
        question: "Comment SENTINELLE m'aide-t-elle pour la régularisation annuelle des charges ?",
        reponse: (
          <div className="space-y-2">
            <p>
              Chaque année, vous devez régulariser les charges locatives avec votre locataire : 
              comparer les provisions versées avec les charges réelles, et rembourser ou 
              réclamer la différence. C'est un calcul complexe en copropriété.
            </p>
            <p>
              SENTINELLE automatise ce calcul en croisant deux sources :
            </p>
            <ul className="list-disc list-inside space-y-1 text-gray-600">
              <li>Vos appels de fonds de copropriété (analysés dans le module Vérification comptable)</li>
              <li>Vos tantièmes et votre quote-part dans chaque clé de répartition</li>
            </ul>
            <p>
              Elle identifie les charges récupérables sur votre locataire (entretien parties communes, 
              TEOM, eau froide collective...) et celles qui restent à votre charge. Vous obtenez 
              un tableau clair poste par poste pour la régularisation.
            </p>
          </div>
        ),
      },
      {
        question: "Qu'est-ce que la TEOM et comment la récupérer sur mon locataire ?",
        reponse: (
          <div className="space-y-2">
            <p>
              La <strong>TEOM (Taxe d'Enlèvement des Ordures Ménagères)</strong> est une taxe 
              incluse dans votre taxe foncière. Elle est récupérable intégralement sur votre 
              locataire — c'est l'une des rares charges fiscales que vous pouvez lui refacturer.
            </p>
            <p>
              SENTINELLE extrait automatiquement le montant de la TEOM depuis votre avis de 
              taxe foncière quand vous l'archivez dans votre GED. Elle l'intègre ensuite dans 
              le calcul des charges récupérables et vous indique comment la déclarer 
              fiscalement en tant que charge déductible (déductible ET récupérable simultanément).
            </p>
          </div>
        ),
      },
    ],
  },
  {
    titre: 'Abonnement & facturation',
    icone: <CreditCard className="w-5 h-5" />,
    couleur: 'violet',
    items: [
      {
        question: "Comment fonctionne l'offre Fondateur ?",
        reponse: (
          <p>
            L'offre Fondateur est réservée aux 20 premiers inscrits. Elle vous garantit un tarif 
            préférentiel <strong>verrouillé à vie</strong> (5,90 €/mois ou 59 €/an au lieu de 14,90 €/mois 
            ou 149 €/an). Vous bénéficiez exactement des mêmes fonctionnalités que l'offre normale : 
            SENTINELLE, 100 analyses/an, tous les modules. La seule différence est le prix.
          </p>
        ),
      },
      {
        question: 'Puis-je résilier à tout moment ?',
        reponse: (
          <div className="space-y-2">
            <p>
              Oui, vous pouvez résilier votre abonnement à tout moment. L'abonnement reste actif 
              jusqu'à la fin de la période en cours (mois ou année selon votre formule). 
              Aucun remboursement au prorata ne sera effectué pour la période restante.
            </p>
            <p>
              <strong>Après la résiliation</strong>, vous disposez de <strong>30 jours</strong> pour 
              exporter vos données via le menu "Exporter mes données". Passé ce délai, toutes vos 
              données (documents, courriers, signalements, conversations, contacts, échéances) seront 
              définitivement supprimées.
            </p>
            <p>
              <strong>Attention :</strong> si vous bénéficiez du tarif Fondateur, la résiliation entraîne 
              la perte définitive de ce tarif. Une réinscription ultérieure sera au tarif Normal en vigueur.
            </p>
          </div>
        ),
      },
      {
        question: "Ai-je un droit de rétractation ?",
        reponse: (
          <div className="space-y-2">
            <p>
              Oui. Conformément au Code de la consommation, vous disposez d'un délai de 
              <strong> 14 jours</strong> à compter de votre souscription pour vous rétracter, 
              sans motif ni pénalité. Le remboursement est intégral et effectué sous 14 jours.
            </p>
            <p>
              Si vous avez déjà utilisé SENTINELLE pendant ce délai, le remboursement pourra être 
              réduit au prorata des analyses consommées. Pour exercer ce droit, envoyez un email 
              à <strong>contact@copro-protect.fr</strong>.
            </p>
          </div>
        ),
      },
      {
        question: "J'ai épuisé mon quota d'analyses. Comment en acheter plus ?",
        reponse: (
          <div className="space-y-2">
            <p>
              Quand votre quota de 100 analyses annuelles est atteint, SENTINELLE vous le signale 
              et vous propose un <strong>pack de recharge de 100 analyses à 29 €</strong>. Le paiement 
              se fait via Stripe, comme votre abonnement.
            </p>
            <p>
              Les analyses achetées en recharge sont valables jusqu'à la fin de votre période 
              d'abonnement en cours. Elles ne sont pas reportées sur la période suivante.
            </p>
          </div>
        ),
      },
      {
        question: 'Comment passer de la formule mensuelle à annuelle ?',
        reponse: (
          <p>
            Rendez-vous sur la page <Link to="/vigie/nous-contacter" className="text-blue-600 hover:text-blue-800 font-medium underline">Avis & idées</Link> pour 
            effectuer la demande. Nous travaillons à rendre cette opération disponible 
            en libre-service dans une prochaine version.
          </p>
        ),
      },
    ],
  },
  {
    titre: 'Sécurité & assistance',
    icone: <Lock className="w-5 h-5" />,
    couleur: 'red',
    items: [
      {
        question: 'Mes données sont-elles sécurisées ?',
        reponse: (
          <div className="space-y-2">
            <p>
              Oui. Vos données sont hébergées en Europe sur l'infrastructure Google Cloud (Firebase), 
              qui est certifiée ISO 27001, SOC 2 et SOC 3. Toutes les communications sont chiffrées 
              en transit (HTTPS) et les données sont chiffrées au repos.
            </p>
            <p>
              Chaque utilisateur VIGIE a un espace totalement isolé : personne — ni un autre utilisateur, 
              ni même l'équipe Copro Protect — ne peut accéder à vos documents ou vos correspondances. 
              Vos fichiers et vos données vous appartiennent.
            </p>
          </div>
        ),
      },
      {
        question: "SENTINELLE ne répond pas ou met longtemps à répondre",
        reponse: (
          <p>
            L'analyse d'un document volumineux peut prendre quelques secondes (10 à 20 secondes 
            pour un PDF de plusieurs pages). Si SENTINELLE ne répond toujours pas après 30 secondes, 
            rafraîchissez la page et réessayez. Si le problème persiste, rendez-vous sur la page{' '}
            <Link to="/vigie/nous-contacter" className="text-blue-600 hover:text-blue-800 font-medium underline">Avis & idées</Link>{' '}
            pour nous signaler le souci.
          </p>
        ),
      },
      {
        question: "Je ne retrouve plus un document que j'ai archivé",
        reponse: (
          <div className="space-y-2">
            <p>
              Rendez-vous dans <strong>Mes documents</strong> — vos documents sont classés par catégorie 
              (juridique, comptable, technique, syndic...). Dépliez chaque catégorie pour parcourir 
              les documents qu'elle contient.
            </p>
            <p>
              Vous pouvez également demander à SENTINELLE : "Où est mon document sur [sujet] ?" 
              et elle vous aidera à le retrouver grâce à sa connaissance de votre portefeuille documentaire.
            </p>
          </div>
        ),
      },
      {
        question: "Comment signaler un bug ou un problème ?",
        reponse: (
          <p>
            Rendez-vous sur la page{' '}
            <Link to="/vigie/nous-contacter" className="text-blue-600 hover:text-blue-800 font-medium underline">Avis & idées</Link>{' '}
            dans le menu de gauche, puis utilisez le formulaire de suggestion. Décrivez le problème 
            rencontré avec le plus de détails possible (page concernée, ce que vous avez fait, 
            ce qui s'est passé). Nous analysons chaque retour.
          </p>
        ),
      },
      {
        question: "Comment exporter mes données personnelles ?",
        reponse: (
          <div className="space-y-2">
            <p>
              Conformément au RGPD (article 20 — droit à la portabilité), vous pouvez exporter 
              l'intégralité de vos données en un clic depuis le menu <strong>Exporter mes données</strong> 
              dans la barre latérale.
            </p>
            <p>
              L'export génère un fichier ZIP contenant :
            </p>
            <ul className="list-disc list-inside space-y-1 text-gray-600">
              <li>Vos documents originaux (PDF, images)</li>
              <li>Vos courriers (entrants et sortants avec pièces jointes)</li>
              <li>Vos signalements et photos</li>
              <li>Vos contacts, dossiers, échéances</li>
              <li>Votre historique de conversations avec SENTINELLE</li>
              <li>Votre profil et celui de votre copropriété</li>
            </ul>
            <p>
              Les données sont fournies en format JSON (lisible par toute application) et CSV 
              (ouvrable dans Excel). Un export est possible toutes les 24 heures.
            </p>
          </div>
        ),
      },
      {
        question: "Puis-je demander la suppression de mon compte et de mes données ?",
        reponse: (
          <div className="space-y-2">
            <p>
              Oui. Conformément au RGPD (article 17 — droit à l'effacement), vous pouvez demander 
              la suppression de votre compte et de toutes vos données en envoyant un email 
              à <strong>contact@copro-protect.fr</strong>.
            </p>
            <p>
              Nous vous recommandons d'exporter vos données avant la demande de suppression 
              (via le menu "Exporter mes données"). Une fois la suppression effectuée, 
              vos données ne pourront plus être récupérées.
            </p>
            <p>
              DOM TECH & SERVICES s'engage à traiter votre demande dans un délai maximum de 30 jours.
            </p>
          </div>
        ),
      },
      {
        question: "Qui a accès à mes données chez Copro Protect ?",
        reponse: (
          <div className="space-y-2">
            <p>
              Vos données sont traitées par trois sous-traitants, chacun dans un périmètre strictement défini :
            </p>
            <ul className="list-disc list-inside space-y-1 text-gray-600">
              <li><strong>Google Cloud (Firebase)</strong> — hébergement et stockage, exclusivement en Europe (Paris)</li>
              <li><strong>Anthropic (Claude)</strong> — traitement IA pour SENTINELLE. Vos données ne sont jamais utilisées pour entraîner les modèles.</li>
              <li><strong>Stripe</strong> — paiement sécurisé. Copro Protect ne stocke aucune donnée bancaire.</li>
            </ul>
            <p>
              L'équipe Copro Protect n'accède à vos données que pour le support technique, 
              sur votre demande explicite. Aucun accès commercial ou marketing à vos contenus.
            </p>
          </div>
        ),
      },
    ],
  },
];

// ============================================
// COULEURS PAR THÈME
// ============================================

const couleurMap: Record<string, { bg: string; text: string; border: string; bgLight: string }> = {
  blue:    { bg: 'bg-blue-100',    text: 'text-blue-600',    border: 'border-blue-200',    bgLight: 'bg-blue-50' },
  indigo:  { bg: 'bg-indigo-100',  text: 'text-indigo-600',  border: 'border-indigo-200',  bgLight: 'bg-indigo-50' },
  emerald: { bg: 'bg-emerald-100', text: 'text-emerald-600', border: 'border-emerald-200', bgLight: 'bg-emerald-50' },
  amber:   { bg: 'bg-amber-100',   text: 'text-amber-600',   border: 'border-amber-200',   bgLight: 'bg-amber-50' },
  violet:  { bg: 'bg-violet-100',  text: 'text-violet-600',  border: 'border-violet-200',  bgLight: 'bg-violet-50' },
  red:     { bg: 'bg-red-100',     text: 'text-red-600',     border: 'border-red-200',     bgLight: 'bg-red-50' },
  teal:    { bg: 'bg-teal-100',    text: 'text-teal-600',    border: 'border-teal-200',    bgLight: 'bg-teal-50' },
  cyan:    { bg: 'bg-cyan-100',    text: 'text-cyan-600',    border: 'border-cyan-200',    bgLight: 'bg-cyan-50' },
};

// ============================================
// COMPOSANT : ITEM FAQ (accordéon)
// ============================================

const FAQAccordeonItem: React.FC<{
  item: FAQItem;
  isOpen: boolean;
  onToggle: () => void;
}> = ({ item, isOpen, onToggle }) => {
  return (
    <div className="border-b border-gray-100 last:border-b-0">
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between py-4 px-1 text-left hover:bg-gray-50 transition-colors rounded-lg"
      >
        <span className={`text-sm font-medium pr-4 ${isOpen ? 'text-blue-700' : 'text-gray-800'}`}>
          {item.question}
        </span>
        <ChevronDown
          className={`w-4 h-4 flex-shrink-0 transition-transform duration-200 ${
            isOpen ? 'rotate-180 text-blue-600' : 'text-gray-400'
          }`}
        />
      </button>
      {isOpen && (
        <div className="pb-4 px-1 text-sm text-gray-600 leading-relaxed animate-in fade-in duration-200">
          {item.reponse}
        </div>
      )}
    </div>
  );
};

// ============================================
// COMPOSANT PRINCIPAL
// ============================================

const FAQVigiePage: React.FC = () => {
  const [openItems, setOpenItems] = useState<Set<string>>(new Set());
  const [recherche, setRecherche] = useState('');

  const toggleItem = (key: string) => {
    setOpenItems((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  // Filtrer les questions par recherche
  const categoriesFiltrees = recherche.trim()
    ? faqCategories
        .map((cat) => ({
          ...cat,
          items: cat.items.filter((item) =>
            item.question.toLowerCase().includes(recherche.toLowerCase())
          ),
        }))
        .filter((cat) => cat.items.length > 0)
    : faqCategories;

  const totalQuestions = faqCategories.reduce((acc, cat) => acc + cat.items.length, 0);

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      {/* En-tête */}
      <div className="text-center space-y-3">
        <div className="flex items-center justify-center gap-3">
          <div className="p-3 bg-blue-100 rounded-2xl">
            <HelpCircle className="w-8 h-8 text-blue-600" />
          </div>
        </div>
        <h1 className="text-2xl font-bold text-gray-900">Questions fréquentes</h1>
        <p className="text-gray-500">
          {totalQuestions} réponses pour vous aider à tirer le meilleur de Copro Protect
        </p>
      </div>

      {/* Barre de recherche */}
      <div className="relative">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
        <input
          type="text"
          value={recherche}
          onChange={(e) => setRecherche(e.target.value)}
          placeholder="Rechercher une question..."
          className="w-full pl-12 pr-4 py-3 bg-white border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
      </div>

      {/* Catégories */}
      {categoriesFiltrees.length === 0 ? (
        <div className="text-center py-12">
          <Search className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500">Aucune question ne correspond à votre recherche.</p>
          <p className="text-sm text-gray-400 mt-1">
            Rendez-vous sur la page{' '}
            <Link to="/vigie/nous-contacter" className="text-blue-600 hover:text-blue-800 underline">Avis & idées</Link>{' '}
            pour nous poser votre question directement.
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {categoriesFiltrees.map((categorie, catIdx) => {
            const couleurs = couleurMap[categorie.couleur] || couleurMap.blue;
            return (
              <div
                key={catIdx}
                className={`bg-white rounded-xl border ${couleurs.border} overflow-hidden`}
              >
                {/* Header catégorie */}
                <div className={`flex items-center gap-3 px-5 py-4 ${couleurs.bgLight}`}>
                  <div className={`p-2 ${couleurs.bg} rounded-lg`}>
                    <span className={couleurs.text}>{categorie.icone}</span>
                  </div>
                  <h2 className="font-bold text-gray-900">{categorie.titre}</h2>
                  <span className="ml-auto text-xs text-gray-400">
                    {categorie.items.length} question{categorie.items.length > 1 ? 's' : ''}
                  </span>
                </div>

                {/* Items */}
                <div className="px-5">
                  {categorie.items.map((item, itemIdx) => {
                    const key = `${catIdx}-${itemIdx}`;
                    return (
                      <FAQAccordeonItem
                        key={key}
                        item={item}
                        isOpen={openItems.has(key)}
                        onToggle={() => toggleItem(key)}
                      />
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Footer */}
      <div className="text-center py-6 border-t border-gray-200">
        <p className="text-sm text-gray-500">
          Vous ne trouvez pas la réponse ? Rendez-vous sur la page{' '}
          <Link to="/vigie/nous-contacter" className="text-blue-600 hover:text-blue-800 font-medium underline">Avis & idées</Link>{' '}
          dans le menu de gauche.
        </p>
      </div>
    </div>
  );
};

export default FAQVigiePage;
